import { domainPath } from "@/lib/domainLinks";

import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, PhoneCall, Globe2, ChevronLeft, ChevronRight, Newspaper, GraduationCap, BrainCircuit, Briefcase, Cloud, Code2, FolderKanban, HeartPulse, LineChart, Megaphone, Network, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import HeroSlider from "@/components/HeroSlider";
import TopPrograms from "@/components/TopPrograms";
import CourseCard from "@/components/CourseCard";
import LogoSlider from "@/components/LogoSlider";

const FeaturedAwards = lazy(() => import("@/components/FeaturedAwards"));
const NewsPress = lazy(() => import("@/components/NewsPress"));
const Centres = lazy(() => import("@/components/Centres"));
const SuccessStories = lazy(() => import("@/components/SuccessStories"));
const TestimonialVideos = lazy(() => import("@/components/TestimonialVideos"));
const DOMAIN_ICONS = { BrainCircuit, Briefcase, Cloud, Code2, FolderKanban, HeartPulse, LineChart, Megaphone, Network, ShieldCheck };
const TGrowth = lazy(() => import("@/components/admin/template/DefaultTemplateRenderer").then((m) => ({ default: m.TGrowth })));
import { useSEO } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

export function InstructorsSection({ section }) {
  const ref = useRef(null);
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (items.length === 0) return null;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 300, behavior: "smooth" });
  return (
    <section className="bg-white" data-testid={`section-${section.key}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              <span className="text-rose-600">{section.title_accent}</span>{" "}<span className="text-slate-900">{section.title_rest}</span>
            </h2>
            {section.subtitle && <p className="text-slate-500 mt-3 text-sm sm:text-base">{section.subtitle}</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-rose-300 hover:text-rose-600 transition-colors" data-testid="instructors-prev" aria-label="Previous instructors"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-rose-300 hover:text-rose-600 transition-colors" data-testid="instructors-next" aria-label="Next instructors"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
        <div ref={ref} className="flex gap-5 overflow-x-auto mt-10 pb-2 snap-x" style={{ scrollbarWidth: "none" }}>
          {items.map((t, i) => (
            <div key={i} className="w-64 shrink-0 snap-start rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-shadow" data-testid={`instructor-card-${i}`}>
              {t.photo ? (
                <img src={t.photo} alt={t.name} className="h-60 w-full object-cover" loading="lazy" />
              ) : (
                <div className="h-60 w-full bg-slate-900 flex items-center justify-center"><span className="text-4xl font-extrabold text-white">{(t.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span></div>
              )}
              <div className="p-5">
                <p className="font-bold text-slate-900">{t.name}</p>
                <p className="text-xs text-rose-600 font-semibold mt-1">{t.role}</p>
                {t.company && <p className="text-xs text-slate-400 mt-1">{t.company}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RailsSection({ section, programs }) {
  const rails = (section.items || [])
    .filter((r) => r.visible !== false)
    .map((r) => ({ ...r, progs: r.highlight ? programs.filter((p) => (p.showcase || []).includes(r.highlight)) : programs }))
    .filter((r) => r.progs.length > 0);
  if (rails.length === 0) return null;
  return (
    <div data-testid={`section-${section.key}`}>
      {rails.map((r, ri) => (
        <section key={ri} className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid={`rail-${ri}`}>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{r.name}</h2>
              {r.description && <p className="text-slate-500 mt-2 text-sm sm:text-base">{r.description}</p>}
            </div>
            <Link to="/courses" className="flex items-center gap-1.5 text-sm font-bold text-rose-600 hover:text-rose-700" data-testid={`rail-view-all-${ri}`}>View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {r.progs.slice(0, 6).map((p) => (
              <div key={p.slug} data-testid={`home-program-card-${p.slug}`}><CourseCard program={p} /></div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [cms, setCms] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [support, setSupport] = useState(null);
  const [sections, setSections] = useState({});
  const [allPrograms, setAllPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [freeCourses, setFreeCourses] = useState([]);
  const [posts, setPosts] = useState([]);

  useSEO({
    title: "Training Basket — Job-Ready IT Courses & Global University Programs",
    description: "Industry-led IT training in Data Science, Cloud, Cyber Security & DevOps, plus global university degrees. Placement cell, EMI options, lifetime LMS access.",
    canonical: `${window.location.origin}/`,
  });

  useEffect(() => {
    trackEvent("page_view", { page: "home" });
    api.get("/website/sections/home_content").then((r) => setCms(r.data)).catch(() => {});
    api.get("/website/sections/home_support").then((r) => setSupport(r.data)).catch(() => {});
    api.get("/website/sections").then((r) => {
      const m = {};
      (r.data.sections || []).forEach((s) => { m[s.key] = s; });
      setSections(m);
    }).catch(() => {});
    api.get("/programs").then((r) => setAllPrograms(r.data.programs || [])).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
    api.get("/programs?free=true").then((r) => setFreeCourses(r.data.programs)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data.posts.slice(0, 3))).catch(() => {});
    api.get("/template/growth-section").then((r) => setGrowth(r.data)).catch(() => {});
  }, []);

  const ord = (key, dflt) => {
    const o = sections[key]?.order;
    return typeof o === "number" ? o : dflt;
  };

  return (
    <div data-testid="home-page" className="flex flex-col">
      <div style={{ order: ord("hero_slider", 10) }}><HeroSlider /></div>

      <div style={{ order: ord("partner_logos", 15) }}><LogoSlider /></div>

      <div style={{ order: ord("featured_programs", 20) }} className="min-h-[2361px] lg:min-h-[1114px]"><TopPrograms /></div>

      <div style={{ order: ord("program_rails", 30) }}>{sections.program_rails && <RailsSection section={sections.program_rails} programs={allPrograms} />}</div>

      {sections.domain_grid && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20" style={{ order: ord("domain_grid", 35) }} data-testid="home-domain-grid">
        <p className="text-xs font-semibold uppercase tracking-widest text-rose-600">{sections.domain_grid.subtitle || "Explore by domain"}</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-2 mb-8">{sections.domain_grid.title || "Pick your track"}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 min-h-[790px] sm:min-h-[660px] lg:min-h-[285px]">
          {categories.slice(0, 10).map((c) => {
            const Icon = DOMAIN_ICONS[c.icon] || GraduationCap;
            return (
              <Link key={c.slug} to={domainPath(c.slug)} data-testid={`domain-tile-${c.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-rose-300 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <span className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:bg-rose-600 group-hover:text-white transition-colors"><Icon className="w-5 h-5" /></span>
                <p className="font-bold text-slate-900 text-sm mt-3 leading-snug">{c.name}</p>
                <p className="text-xs text-slate-400 mt-1">{c.program_count || ""} programs</p>
              </Link>
            );
          })}
        </div>
      </section>
      )}

      {freeCourses.length > 0 && sections.free_course_strip && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16" style={{ order: ord("free_course_strip", 36) }}>
          <div className="rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-testid="free-course-strip">
            <div>
              <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-white/20 border border-white/25 uppercase tracking-widest">Free course</span>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-4">{freeCourses[0].title}</h2>
              <p className="text-emerald-100 mt-3 text-sm sm:text-base leading-relaxed">{freeCourses[0].description}</p>
              <Link to={freeCourses[0].url_path || `/courses/${freeCourses[0].slug}`}>
                <Button className="mt-6 bg-white text-emerald-700 hover:bg-emerald-50 font-bold rounded-xl" data-testid="free-course-cta">Start learning free</Button>
              </Link>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-3">
              {(freeCourses[0].highlights || []).map((h, i) => (
                <div key={i} className="bg-white/10 border border-white/15 rounded-2xl p-4 text-sm font-semibold">{h}</div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div style={{ order: ord("instructors", 40) }}>{sections.instructors && <InstructorsSection section={sections.instructors} />}</div>

      <div style={{ order: ord("home_content", 50) }}>
        <Suspense fallback={null}>
          <TestimonialVideos cms={cms} />
          <SuccessStories cms={cms} />
        </Suspense>
      </div>

      {sections.jobbasket_strip && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16" style={{ order: ord("jobbasket_strip", 55) }}>
        <div className="group rounded-3xl bg-gradient-to-r from-amber-50 via-[#FFFBEB] to-white ring-1 ring-amber-100 px-6 sm:px-8 py-6 sm:py-7 flex flex-col sm:flex-row items-center justify-between gap-5 shadow-[0_10px_40px_-18px_rgba(245,179,1,0.35)] hover:shadow-[0_18px_50px_-16px_rgba(245,179,1,0.5)] transition-shadow duration-300" data-testid="jobbasket-strip">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-xl bg-[#17263B] flex items-center justify-center text-sm font-extrabold text-[#FFC72C] shrink-0">JB</span>
            <div>
              <p className="font-bold text-slate-900 text-sm sm:text-base">Placements powered by <span className="text-amber-600">Job Basket</span></p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Every Training Basket program connects you to our parent placement platform — jobs, ATS resumes and recruiter drives.</p>
            </div>
          </div>
          <a href="https://jobbasket.in" target="_blank" rel="noopener noreferrer" data-testid="jobbasket-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-bold px-7 py-3.5 text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-sm shrink-0">
            Explore jobs on Job Basket <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </section>
      )}

      <div style={{ order: ord("home_growth", 60) }}>{growth?.enabled && <Suspense fallback={null}><TGrowth c={growth.content} onApply={() => navigate("/courses")} /></Suspense>}</div>

      <div style={{ order: ord("awards", 70) }}><Suspense fallback={null}><FeaturedAwards /></Suspense></div>

      <div style={{ order: ord("press", 80) }}><Suspense fallback={null}><NewsPress /></Suspense></div>

      {posts.length > 0 && sections.home_blog && (
        <section className="relative bg-white overflow-hidden" style={{ order: ord("home_blog", 85) }} data-testid="home-guides-section">
          <div className="absolute -left-8 top-16 w-28 h-28 opacity-60 hidden lg:block" style={{ backgroundImage: "radial-gradient(#CBD5E1 1.6px, transparent 1.6px)", backgroundSize: "16px 16px" }} aria-hidden="true" />
          <div className="absolute right-10 top-10 hidden lg:block text-right select-none" aria-hidden="true">
            <p className="font-serif italic text-2xl leading-snug text-slate-300 -rotate-3">Learn<br />Grow<br /><span className="text-slate-400">Get Hired</span></p>
            <svg className="w-16 h-6 ml-auto mt-1 text-[#F5B301]" viewBox="0 0 64 24" fill="none" aria-hidden="true"><path d="M4 6 C 24 20, 44 20, 58 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /><path d="M52 4 L 60 8 L 53 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
            <div className="text-center max-w-2xl mx-auto">
              <span className="inline-block rounded-full bg-[#FFC72C] text-[#17263B] text-xs font-extrabold uppercase tracking-[0.15em] px-4 py-1.5" data-testid="blog-eyebrow">Our Blog</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#17263B] mt-4 leading-tight" data-testid="blog-title">
                Training Basket{" "}
                <span className="relative inline-block text-[#F5B301] italic">Blog
                  <svg className="absolute left-0 -bottom-1.5 w-full" viewBox="0 0 120 10" preserveAspectRatio="none" aria-hidden="true"><path d="M2 7 Q 60 1 118 6" stroke="#F5B301" strokeWidth="3.5" strokeLinecap="round" fill="none" /></svg>
                </span>
              </h2>
              <p className="text-sm sm:text-base text-slate-500 mt-3" data-testid="blog-subtitle">{sections.home_blog.subtitle || "Explore expert insights, industry trends, certification guides and career tips to help you stay ahead in your professional journey."}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col" data-testid={`post-card-${p.slug}`}>
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    {p.image ? (
                      <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#17263B] to-slate-600 flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-white/25" aria-hidden="true" />
                      </div>
                    )}
                    {p.category && <span className="absolute top-3 left-3 rounded-full bg-[#FFC72C] text-[#17263B] text-[10px] font-extrabold uppercase tracking-wide px-3 py-1.5 shadow" data-testid={`post-category-${p.slug}`}>{p.category}</span>}
                  </div>
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <h3 className="font-extrabold text-slate-900 leading-snug line-clamp-2">{p.title}</h3>
                    <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-2.5 mt-4 text-sm font-bold text-[#17263B]" data-testid={`post-read-more-${p.slug}`}>
                      Read More
                      <span className="w-8 h-8 rounded-full bg-[#FFC72C] group-hover:bg-[#F5B800] flex items-center justify-center transition-colors">
                        <ArrowRight className="w-4 h-4 text-[#17263B] group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/blog" className="inline-flex items-center gap-2.5 rounded-full bg-[#FFFBEB] ring-1 ring-amber-200 hover:ring-amber-300 px-7 py-3.5 text-sm font-semibold text-slate-700 shadow-sm hover:-translate-y-0.5 transition-all" data-testid="blog-view-all-cta">
                <Newspaper className="w-4 h-4 text-amber-500" /> Check out our blog for free insights and updates on your field
                <ArrowRight className="w-4 h-4 text-amber-500" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <div style={{ order: ord("centres", 90) }}><Suspense fallback={null}><Centres /></Suspense></div>

      {support && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 border-t border-slate-100" style={{ order: ord("home_support", 100) }} data-testid="home-support-section">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            {support.brand_name || "Training Basket"} <span className="text-rose-600">{support.heading_accent || "Learner Support"}</span>
          </h2>
          <p className="text-slate-500 mt-3 text-sm sm:text-base">{support.subtext}</p>
          <div className="flex flex-wrap items-center gap-x-14 gap-y-6 mt-8">
            <div className="flex flex-wrap items-center gap-4">
              <span className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-slate-200" aria-hidden="true">
                <svg viewBox="0 0 36 36" className="w-full h-full"><rect width="36" height="12" fill="#FF9933" /><rect y="12" width="36" height="12" fill="#FFFFFF" /><rect y="24" width="36" height="12" fill="#138808" /><circle cx="18" cy="18" r="3.5" fill="none" stroke="#000080" strokeWidth="1.5" /></svg>
              </span>
              <span className="font-bold text-slate-900 text-sm sm:text-base">{support.indian_label || "Indian Nationals"}</span>
              <a href={`tel:${(support.indian_phone || "").replace(/\s/g, "")}`} data-testid="support-indian-phone"
                className="flex items-center gap-2 rounded-xl border border-rose-300 px-5 py-3 font-extrabold text-slate-900 hover:bg-rose-50 transition-colors">
                <PhoneCall className="w-4 h-4 text-rose-600" /> {support.indian_phone}
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="w-9 h-9 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center shrink-0"><Globe2 className="w-5 h-5" /></span>
              <span className="font-bold text-slate-900 text-sm sm:text-base">{support.foreign_label || "Foreign Nationals"}</span>
              <a href={`tel:${(support.foreign_phone || "").replace(/\s/g, "")}`} data-testid="support-foreign-phone"
                className="flex items-center gap-2 rounded-xl border border-rose-300 px-5 py-3 font-extrabold text-slate-900 hover:bg-rose-50 transition-colors">
                <PhoneCall className="w-4 h-4 text-rose-600" /> {support.foreign_phone}
              </a>
            </div>
          </div>
        </section>
      )}

    </div>
  );
}
